import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles } from "@material-ui/core/styles";
import { AccountingDateFilter } from "../../../Utils/AccountingUtils/AccountingDateFilter";
import { formatAmount, formatDigits } from "../../../Utils/formatNumbers";
import { dateConvert, formatDate, formatLocalTime } from "../../../Utils/ConvertDate";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { exportPDF } from "../../../Utils/exportPDF";
import CustomDialog from "../../../Utils/Dialog/CustomDialog";
import { usePrint } from "../../../Utils/Template/usePrint";
import { SpecificJournalTemplate } from "./SpecificJournal";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", overflowX: "auto" },

  tableHeader: {
    fontWeight: "bold",
    background: "#f5f5f5",
    padding: theme.spacing(1.5),
    display: "grid",
    gridTemplateColumns: "1.2fr 2fr 1fr 1fr 1fr 1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    alignItems: "center",
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1.5fr 1fr 1fr",
    },
  },

  headerCell: {
    textAlign: "left",
    padding: theme.spacing(0, 1),
    "&:nth-child(3), &:nth-child(4), &:nth-child(5), &:nth-child(6)": {
      textAlign: "right",
    },
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1.2fr 2fr 1fr 1fr 1fr 1fr",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderBottom: "1px solid #e0e0e0",
    cursor: "pointer",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1.5fr 1fr 1fr",
    },
  },

  cell: {
    padding: theme.spacing(0, 1),
    textAlign: "left",
    "&:nth-child(3), &:nth-child(4), &:nth-child(5), &:nth-child(6)": {
      textAlign: "right",
    },
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },

  clickableId: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    cursor: "pointer",
    fontWeight: 500,
  },

  accordionDetails: {
    flexDirection: "column",
    padding: theme.spacing(2),
    background: "#fafafa",
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },

  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
}));

const JournalList = ({ actions, item, isResolved }) => {
  const classes = useStyles();
  const currentDate = new Date();
  const { handlePrint, componentRef } = usePrint();

  const [journal, setJournal] = useState([]);
  const [loading, setLoading] = useState("idle");
  const [runningBalance, setRunningBalance] = useState(0);
  const [state, setState] = useState({ from_date: dateConvert(), to_date: dateConvert() });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState("idle");
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(100);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJournalID, setSelectedJournalID] = useState("");

  const { id, head_name } = item;
  const { from_date, to_date } = state;

  useEffect(() => {
    if (id) fetchJournal();
  }, [id, isResolved, activePage, perPage, from_date, to_date]);

  const fetchJournal = async () => {
    setLoading("pending");
    try {
      const res = await axios.post(
        `${config.epharmUrl}/accounting/ledger_details?ledger_id=${id}&start_date=${from_date}&end_date=${to_date}&page=${activePage}&per_page=${perPage}`
      );
      const data = res.data?.data?.[0] || {};
      const list = (data.details || []).map((item) => ({
        account: item.Account,
        credit: item.Credit,
        debit: item.Debit,
        balance: item.balance,
        date: item.Date || item.date,
        details: item.details,
        journalId: item.journal,
      }));
      setJournal(list);
      setRunningBalance(data.openingbalance || 0);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 1);
      setLoading("success");
    } catch (error) {
      setLoading("error");
    }
  };

  const handleChange = (event) => {
    const { value, name } = event.target;
    setState({ ...state, [name]: value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    setActivePage(1);
    fetchJournal();
  };

  const handleJournalClick = (journalId) => {
    setSelectedJournalID(journalId);
    setOpenDialog(true);
  };

  const handleClose = () => setOpenDialog(false);

  const downloadPDF = () => {
    exportPDF(
      `${head_name} Journal(${formatLocalTime(currentDate)})`,
      [["Date", "Description", "Debit", "Credit", "Balance", "Journal Id"]],
      journal.map((item) => [
        formatDate(item.date),
        item.details,
        formatDigits(item.debit),
        formatDigits(item.credit),
        formatDigits(item.balance),
        item.journalId,
      ]),
      `${head_name} Journal(${formatLocalTime(currentDate)}).pdf`,
      [["", "", "", "", "Opening Balance", formatAmount(runningBalance)]]
    );
  };

  const isLoading = loading === "pending";

  return (
    <div className={classes.root}>
      {/* Top Bar */}
      <div className={classes.topBar}>
        <div><strong>Opening Balance:</strong> {formatAmount(runningBalance)}</div>
        <Button variant="contained" color="primary" onClick={downloadPDF} disabled={journal.length === 0}>
          Download PDF
        </Button>
      </div>

      {/* Date Filter */}
      <AccountingDateFilter
        {...{ from_date, to_date, handleChange, submitted, handleSubmit, isPending: isSubmitted === "pending" }}
      />

      {/* Pagination */}
      <div className={classes.paginationBar}>
        <FormControl size="small">
          <InputLabel>Per Page</InputLabel>
          <Select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setActivePage(1); }}>
            {[100, 150, 200, 500, 1000].map((num) => (
              <MenuItem key={num} value={num}>{num}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <div>
          <Button disabled={activePage <= 1} onClick={() => setActivePage(Math.max(activePage - 1, 1))}>
            Previous
          </Button>
          <Typography component="span" style={{ margin: "0 8px" }}>
            Page {activePage} of {totalPages}
          </Typography>
          <Button disabled={activePage >= totalPages} onClick={() => setActivePage(Math.min(activePage + 1, totalPages))}>
            Next
          </Button>
        </div>
      </div>

      {/* Table Header */}
      <div className={classes.tableHeader}>
        <Typography className={classes.headerCell}>Date</Typography>
        <Typography className={classes.headerCell}>Description</Typography>
        <Typography className={classes.headerCell}>Debit</Typography>
        <Typography className={classes.headerCell}>Credit</Typography>
        <Typography className={classes.headerCell}>Balance</Typography>
        <Typography className={classes.headerCell}>Journal ID</Typography>
      </div>

      {/* Journal Entries */}
      {isLoading ? (
        <Typography style={{ padding: "20px", textAlign: "center" }}>Loading...</Typography>
      ) : journal.length === 0 ? (
        <Typography style={{ padding: "20px", textAlign: "center" }}>No journal entries found.</Typography>
      ) : (
        journal.map((row, index) => (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className={classes.row}>
                <div className={classes.cell}>{formatDate(row.date)}</div>
                <div className={classes.cell}>{row.details}</div>
                <div className={classes.cell}>{formatDigits(row.debit)}</div>
                <div className={classes.cell}>{formatDigits(row.credit)}</div>
                <div className={classes.cell}>{formatDigits(row.balance)}</div>
                <div className={classes.cell}>
                  <span
                    className={classes.clickableId}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleJournalClick(row.journalId);
                    }}
                  >
                    {row.journalId}
                  </span>
                </div>
              </div>
            </AccordionSummary>

            <AccordionDetails className={classes.accordionDetails}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <strong>Account:</strong> {row.account}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <strong>Journal ID:</strong>{" "}
                  <span className={classes.clickableId} onClick={() => handleJournalClick(row.journalId)}>
                    {row.journalId}
                  </span>
                </Grid>
                <Grid item xs={12}>
                  <strong>Details / Description:</strong> {row.details}
                </Grid>
                <Grid item xs={6} sm={3} style={{ textAlign: "right" }}>
                  <strong>Debit:</strong> {formatDigits(row.debit)}
                </Grid>
                <Grid item xs={6} sm={3} style={{ textAlign: "right" }}>
                  <strong>Credit:</strong> {formatDigits(row.credit)}
                </Grid>
                <Grid item xs={6} sm={3} style={{ textAlign: "right" }}>
                  <strong>Balance:</strong> {formatDigits(row.balance)}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Specific Journal Modal */}
      <CustomDialog
        open={openDialog}
        handleClose={handleClose}
        title="Specific Journal Report"
        maxWidth="sm"
        isOverflow={false}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button onClick={handlePrint} className="btn pharmacy-info-btn">
            Print
          </button>
        </div>
        <div ref={componentRef}>
          <SpecificJournalTemplate selectedJournalID={selectedJournalID} />
        </div>
      </CustomDialog>
    </div>
  );
};

export default JournalList;